import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { useI18n } from "next-localization";
import Meta from "@/components/meta";
import Navbar from "@/components/navbar";
import useCustomerSet from "@/components/customer_logos";
import Footer from "@/components/footer";
import { useRouter } from "next/router";

function Demo() {
  const { asPath } = useRouter();
  const { t } = useI18n();
  const customers = useCustomerSet("demo");
  const [sendState, setSendState] = useState<"sending" | "sent" | null>(null);

  useEffect(() => {
    if (asPath.endsWith("thanks")) {
      setSendState("sent");
      return;
    }
  }, [asPath]);

  return (
    <>
      <Meta title="Request a demo" />
      <Navbar />
      <main className="grid">
        <div className="left-col">
          <h2>Request a demo</h2>
          <p className="intro">
            We’re excited to show you how Watershed can help you launch and run
            your company’s climate {t("common.program")}.
          </p>
          <h2 style={{ marginTop: 48, marginBottom: 24, fontSize: 24 }}>
            Trusted by
          </h2>
          <div className="customer-logos" style={{ width: "80%" }}>
            {customers.map((customer) => (
              <Image
                key={customer.name}
                alt={customer.name}
                draggable="false"
                src={customer.src}
                width={128}
                height={28}
                objectFit="contain"
                objectPosition="left center"
              />
            ))}
            <style
              dangerouslySetInnerHTML={{
                __html: customers
                  .map(({ scale }, i) =>
                    scale === 1
                      ? undefined
                      : `.customer-logos > span:nth-child(${
                          i + 1
                        }) img { transform: scale(${scale}); }`
                  )
                  .filter(Boolean)
                  .join("\n"),
              }}
            />
          </div>
        </div>
        <div className="right-col">
          <h3 className="mobile-title">Request a demo</h3>
          {sendState === "sent" ? (
            <div style={{ padding: 24 }}>
              <p>Thank you! We’ll email you back soon.</p>
              <button onClick={() => setSendState(null)}>
                Submit another request
              </button>
            </div>
          ) : (
            <form
              action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8"
              method="POST"
            >
              <input type="hidden" name="oid" value="00D8b000001aSkL" />
              <input
                type="hidden"
                name="retURL"
                value="https://watershed.com/demo/thanks"
              />
              <input
                id="lead_source"
                name="lead_source"
                type="hidden"
                value="Inbound: Demo Request"
              />
              <input id="rating" name="rating" type="hidden" value="Hot" />

              {/*
              <input type="hidden" name="debug" value={1} />
              <input
                type="hidden"
                name="debugEmail"
                value="lachlan@watershedclimate.com"
              />
              */}

              <label>
                <div className="title required">First name</div>
                <input
                  autoFocus
                  type="text"
                  name="first_name"
                  placeholder="Jane"
                  autoComplete="given-name"
                  required
                  maxLength={40}
                />
              </label>
              <label>
                <div className="title required">Last name</div>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Smith"
                  autoComplete="family-name"
                  required
                  maxLength={80}
                />
              </label>
              <label>
                <div className="title required">Company name</div>
                <input
                  type="text"
                  name="company"
                  placeholder="My Company"
                  required
                  maxLength={40}
                />
              </label>
              <label>
                <div className="title required">Work email</div>
                <input
                  type="email"
                  name="email"
                  placeholder="jsmith@company.com"
                  autoComplete="email"
                  required
                  maxLength={80}
                />
              </label>
              <label>
                <div className="title">Phone number</div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="(555) 555-5555"
                  autoComplete="tel"
                  maxLength={40}
                />
              </label>
              <p style={{ marginTop: -12 }}>
                We will only use your (optional) phone number to contact you
                about this request, and will never share it.
              </p>
              <label>
                <div className="title">How can we help?</div>
                <textarea name="description" placeholder="Message" />
              </label>
              <div style={{ padding: "8px 0", textAlign: "right" }}>
                <button type="submit" name="submit">
                  Request a demo
                </button>
              </div>
              <p style={{ marginBottom: 0 }}>
                By requesting a demo you agree to Watershed’s{" "}
                <Link href="/privacy">privacy policy</Link>, and consent to
                receive Watershed marketing communications.
              </p>
            </form>
          )}
        </div>
      </main>
      <style jsx>{`
        .grid {
          display: grid;
          grid-template-columns: 4fr 3fr;
          column-gap: 24px;
        }
        .left-col {
          border: 2px solid #edf1f5;
          border-radius: 16px;
          padding: 48px;
        }
        .right-col {
          border-radius: 16px;
          background: #f0f3f7;
          padding: 32px;
        }
        .right-col .mobile-title {
          display: none;
          padding: 0 16px;
        }
        p.intro {
          font-size: 21px;
          line-height: 32px;
        }

        form {
          display: grid;
          grid-gap: 16px;
        }
        form p {
          font-size: 14px;
          padding: 0 16px;
        }
        label {
          display: block;
          margin: 0;
        }
        label .title {
          font-weight: bold;
          color: #222c49;
          margin: 0 16px 4px;
        }
        label .title.required::after {
          content: "*";
          margin-left: 4px;
          font-weight: regular;
          color: #ff8d73;
        }
        input,
        textarea {
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          background-color: #e3e7ee;
          font: inherit;
          font-size: 16px;
          display: block;
          width: 100%;
          margin-bottom: 4px;
        }
        input:focus,
        textarea:focus {
          outline: none;
          box-shadow: 0 0 0 2px inset #255dff;
        }
        textarea {
          height: 120px;
        }
        button {
          cursor: pointer;
          padding: 0 16px;
          height: 32px;
          border: 0;
          border-radius: 16px;
          font-family: inherit;
          font-weight: bold;
          font-size: 15px;
          background: #265cff;
          color: #fff;
          display: inline-block;
        }
        button:focus {
          outline: none;
          box-shadow: 0 0 0 2px inset #001f79;
        }
        button:disabled {
          cursor: default;
          opacity: 0.5;
        }

        .customer-logos {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-gap: 32px;
          align-items: center;
        }
        .customer-logos :global(> span) {
          overflow: visible !important;
        }
        .customer-logos :global(img) {
          transform-origin: left center;
          object-fit: contain;
        }

        @media screen and (max-width: 900px) {
          .grid {
            grid-template-columns: 1fr;
          }
          .left-col {
            display: none;
          }
          .right-col {
            padding: 24px;
          }
          .right-col .mobile-title {
            display: block;
          }
        }

        @media screen and (min-width: 520px) {
          label,
          form div,
          form p {
            grid-column: span 2;
          }
          label:first-of-type,
          label:nth-of-type(2) {
            grid-column: span 1;
          }
          form {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
      <Footer />
    </>
  );
}

export default Demo;

export { getStaticProps } from "@/lib/locales";
